import { createSignal, Show, VoidComponent } from "solid-js";
import { useTranslation } from "../../../../../state/i18n";
import SettingSection from "../../components/SettingSection";
import { EditableSettingTextValue } from "../../components/SettingValue";
import SettingSectionEditButton from "../../components/SettingSectionEditButton";
import SettingSectionEditActions from "../../components/SettingSectionEditActions";
import { createForm } from "../../../../../components/form/createForm";
import { object } from "valibot";
import { MAX_CHARACTERS_SM, MAX_CHARACTERS_XS } from "../../../../../constants";
import { requireText, email, optionalTextValue } from "../../../../../utils/validation/base";
import { BillingProfileDto } from "../../../../../services/BillingService";

type BillingInformationSectionProps = {
  billingProfile: BillingProfileDto;
};

// TODO: DEV-8319 & DEV-8320
const BillingInformationSection: VoidComponent<BillingInformationSectionProps> = (props) => {
  const { t } = useTranslation();

  const [editing, setEditing] = createSignal(false);

  const form = createForm(
    object({
      companyName: requireText(MAX_CHARACTERS_SM, true),
      email: email(MAX_CHARACTERS_SM),
      phoneNumber: requireText(MAX_CHARACTERS_SM, true),
      address: requireText(MAX_CHARACTERS_SM, true),
      zipCode: requireText(MAX_CHARACTERS_XS, true),
      country: requireText(MAX_CHARACTERS_SM, true),
      city: requireText(MAX_CHARACTERS_SM, true),
      vat: optionalTextValue(MAX_CHARACTERS_SM),
    }),
    async (_value) => {
      // TODO
    },
    {
      companyName: props.billingProfile.companyName ?? "",
      email: props.billingProfile.billingEmailAddress ?? "",
      phoneNumber: props.billingProfile.phoneNumber ?? "",
      address: props.billingProfile.billingAddress.address ?? "",
      zipCode: props.billingProfile.billingAddress.zipCode ?? "",
      country: props.billingProfile.billingAddress.country ?? "",
      city: props.billingProfile.billingAddress.city ?? "",
      vat: `${props.billingProfile.vatNumber ?? ""}`,
    },
  );

  return (
    <SettingSection
      ariaIdPrefix="billing"
      title={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_TITLE)}
      description={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_DESCRIPTION)}
    >
      <div class="flex min-w-0 grow flex-col gap-6">
        <EditableSettingTextValue
          editing={editing()}
          label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_COMPANY_NAME_LABEL)}
          text={props.billingProfile.companyName ?? ""}
          editValue={form.getValue("companyName")}
          editError={form.getError("companyName")}
          editDisabled={false}
          editOnBlur={(value) => form.updateValue("companyName", value)}
          required
        />

        <div class="flex gap-6">
          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_EMAIL_LABEL)}
              text={props.billingProfile.billingEmailAddress ?? ""}
              editValue={form.getValue("email")}
              editError={form.getError("email")}
              editDisabled={false}
              editOnBlur={(value) => form.updateValue("email", value)}
              required
            />
          </div>
          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_PHONE_NUMBER_LABEL)}
              text={props.billingProfile.phoneNumber ?? ""}
              editValue={form.getValue("phoneNumber")?.toString()}
              editError={form.getError("phoneNumber")}
              editDisabled={false}
              editOnBlur={(value) => form.updateValue("phoneNumber", value)}
              required
            />
          </div>
        </div>

        <div class="flex gap-6">
          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_ADDRESS_LABEL)}
              text={props.billingProfile.billingAddress.address ?? ""}
              editValue={form.getValue("address")}
              editError={form.getError("address")}
              editDisabled={false}
              editOnBlur={(value) => form.updateValue("address", value)}
              required
            />
          </div>
          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_ZIP_CODE_LABEL)}
              text={props.billingProfile.billingAddress.zipCode ?? ""}
              editValue={form.getValue("zipCode")}
              editError={form.getError("zipCode")}
              editDisabled={false}
              editOnBlur={(value) => form.updateValue("zipCode", value)}
              required
            />
          </div>
        </div>
        <div class="flex gap-6">
          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_COUNTRY_LABEL)}
              text={props.billingProfile.billingAddress.country ?? ""}
              editValue={form.getValue("country")}
              editError={form.getError("country")}
              editDisabled={false}
              editOnBlur={(value) => form.updateValue("country", value)}
              required
            />
          </div>
          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_CITY_LABEL)}
              text={props.billingProfile.billingAddress.city ?? ""}
              editValue={form.getValue("city")}
              editError={form.getError("city")}
              editDisabled={false}
              editOnBlur={(value) => form.updateValue("city", value)}
              required
            />
          </div>
        </div>
        <Show when={props.billingProfile.vatNumber || editing()}>
          <EditableSettingTextValue
            editing={editing()}
            label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_VAT_LABEL)}
            text={`${props.billingProfile.vatNumber ?? ""}`}
            editValue={form.getValue("vat")}
            editError={form.getError("vat")}
            editDisabled={false}
            editOnBlur={(value) => form.updateValue("vat", value)}
          />
        </Show>

        <SettingSectionEditActions
          editing={editing()}
          form={form}
          cancelLabel={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_CANCEL_BUTTON)}
          saveLabel="Save"
          savingLabel="Saving"
          stopEditing={() => setEditing(false)}
        />
      </div>

      <SettingSectionEditButton
        editing={editing()}
        editLabel={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_EDIT_BUTTON)}
        setEditing={() => setEditing(true)}
      />
    </SettingSection>
  );
};

export default BillingInformationSection;
